import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import axios from "axios";

const Faq = () => {
  const [active, setActive] = useState(0);

  // =========================
  // FORM STATES
  // =========================
  const [formData, setFormData] = useState({
    email: "",
    countryCode: "+44",
    phone: "",
  });

  const [loading, setLoading] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // =========================
  // HANDLE INPUT CHANGE
  // =========================
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // =========================
  // HANDLE SUBMIT
  // =========================
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      setErrorMessage("");

      const payload = {
        email: formData.email,
        phone: `${formData.countryCode}${formData.phone}`,
      };

      const response = await axios.post(
        "https://api.clinicalaiassistance.com/api/subscriber/subscribe",
        payload
      );

      if (response.data.success) {
        // Show Success Popup
        setShowPopup(true);

        // Auto Close Popup
        setTimeout(() => {
          setShowPopup(false);
        }, 3000);

        // Reset Form
        setFormData({
          email: "",
          countryCode: "+44",
          phone: "",
        });
      }
    } catch (error) {
      setErrorMessage(
        error.response?.data?.message || "Something went wrong"
      );
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // FAQ DATA
  // =========================
  const faqs = [
    {
      question: "What does Clinical AI Assistance do?",
      answer:
        "Clinical AI Assistance helps doctors across the full clinical journey: pre-consultation symptom intake, live consultation transcription, and post-consultation reporting.",
    },
    {
      question: "How does Dr. Pre-Consultant help before the consultation?",
      answer:
        "Dr. Pre-Consultant acts as an AI-based first touchpoint for patients, collecting symptom details through conversational AI and preparing a preliminary report for the physician.",
    },
    {
      question:
        "What does Pre-ConsultantVaani do during and after the consultation?",
      answer:
        "Pre-ConsultantVaani uses voice recognition and transcription to generate structured, editable clinical notes in real time and summarizes the encounter into reports.",
    },
    {
      question: "Who is the product for?",
      answer:
        "The product is primarily aimed at clinicians, hospitals, and healthcare professionals rather than general consumers.",
    },
    {
      question:
        "What results or benefits does Clinical AI Assistance claim?",
      answer:
        "The main benefits presented are reduced doctor admin burden, faster consultations, better workflow efficiency, and improved documentation quality.",
    },
  ];

  return (
    <div className=" dark:bg-black text-black dark:text-white transition-colors">
      {/* ========================= */}
      {/* FAQ SECTION */}
      {/* ========================= */}
      <section className=" max-w-3xl mx-auto px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold">
            Frequently Asked Questions
          </h2>

          <p className="mt-3 text-gray-500 dark:text-gray-400">
            Find answers to common questions about Clinical AI Assistance AI
          </p>
        </motion.div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="border-b border-gray-200 dark:border-gray-800 pb-4"
            >
              <button
                className="w-full flex justify-between items-center text-left py-4 font-medium"
                onClick={() => setActive(active === index ? null : index)}
              >
                {faq.question}

                <motion.span
                  animate={{ rotate: active === index ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                  className="text-xl"
                >
                  {active === index ? "−" : "+"}
                </motion.span>
              </button>

              <AnimatePresence>
                {active === index && (
                  <motion.p
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.35 }}
                    className="text-gray-600 dark:text-gray-400 pb-4 overflow-hidden"
                  >
                    {faq.answer}
                  </motion.p>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ========================= */}
      {/* CTA SECTION */}
      {/* ========================= */}
      <section id="subscription" className="px-4 sm:px-6 pb-16">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
          className="max-w-6xl mx-auto  rounded-3xl 
          text-center py-12 sm:py-16 px-4 sm:px-8 text-black relative overflow-hidden"
        >
          {/* Heading */}
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-2xl sm:text-3xl md:text-5xl font-semibold leading-tight dark:text-white"
          >
            Subscribe to get <br />
Important updates
          </motion.h2>

          {/* Description */}
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="max-w-2xl mx-auto mt-5 text-sm sm:text-base dark:text-white text-black"
          >
           Join 2,000+ subscribers for weekly updates on medical technologies and AI advancements.
          </motion.p>

          {/* FORM */}
          <motion.form
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="mt-10 flex flex-col lg:flex-row gap-4 max-w-5xl mx-auto items-center"
          >
            {/* Email */}
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter your email"
              required
              className="w-full lg:flex-1 px-5 py-3 rounded-full 
              text-black outline-none border border-white/20
              focus:ring-2 focus:ring-white"
            />

            {/* Phone Section */}
            <div
              className="w-full lg:flex-1 flex items-center bg-white rounded-full 
              border border-white/20 overflow-hidden"
            >
              {/* Country Code */}
              <select
                name="countryCode"
                value={formData.countryCode}
                onChange={handleChange}
                className="px-4 py-3 bg-transparent text-black outline-none"
              >
                <option value="+44">🇬🇧 +44</option>
                <option value="+91">🇮🇳 +91</option>
                <option value="+1">🇺🇸 +1</option>
                <option value="+61">🇦🇺 +61</option>
                <option value="+971">🇦🇪 +971</option>
              </select>

              {/* Divider */}
              <div className="h-6 w-[1px] bg-gray-300"></div>

              {/* Phone Input */}
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Enter phone number"
                required
                className="w-full px-4 py-3 text-black outline-none"
              />
            </div>

            {/* Button */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              type="submit"
              disabled={loading}
              className="w-full lg:w-auto bg-white text-[#106EE8] 
              px-8 py-3 rounded-full font-semibold shadow-lg
              hover:bg-gray-100 transition whitespace-nowrap"
            >
              {loading ? "Submitting..." : "Subscribe"}
            </motion.button>
          </motion.form>

          {/* Error Message */}
          {errorMessage && (
            <p className="mt-4 text-red-200 text-sm">
              {errorMessage}
            </p>
          )}
        </motion.div>
      </section>

      {/* ========================= */}
      {/* SUCCESS POPUP */}
      {/* ========================= */}
<AnimatePresence>
  {showPopup && (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4"
    >
      <motion.div
        initial={{ scale: 0.8, opacity: 0, y: 30 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.8, opacity: 0, y: 30 }}
        transition={{ duration: 0.3 }}
        className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full text-center"
      >
        {/* Success Icon */}
        <div
          className="w-20 h-20 mx-auto rounded-full bg-green-100 
          flex items-center justify-center text-4xl"
        >
          ✅
        </div>

        {/* Title */}
        <h3 className="mt-6 text-2xl font-bold text-[#106EE8]">
          Successfully Subscribed!
        </h3>

        {/* Message */}
        <p className="mt-3 text-gray-600 text-sm sm:text-base">
          Your subscription request has been submitted successfully. Our team will review it and get in touch with you shortly.
        </p>

        {/* Button */}
        <button
          onClick={() => setShowPopup(false)}
          className="mt-6 bg-[#106EE8] text-white px-6 py-3 rounded-full 
          font-medium hover:opacity-90 transition"
        >
          Close
        </button>
      </motion.div>
    </motion.div>
  )}
</AnimatePresence>
    </div>
  );
};

export default Faq;